import { applyOverrides, createOverride, createTask } from "@app/core";
import { getServices } from "../../platform.js";
import { buildEffectiveTaskFields, upsertSingleOverride } from "./helpers.js";
import type { DetachPatch, SeriesGet, SeriesSet } from "./types.js";

/**
 * True detachment: spawn a standalone Task row for the occurrence and cancel
 * the series slot. The new task has recurrence_id=null so it is completely
 * isolated from all future series edits and never triggers the recurring-confirm
 * dialog when edited again.
 */
export async function detachOccurrence(
  set: SeriesSet,
  get: SeriesGet,
  sourceId: string,
  occurrenceIso: string,
  patch: DetachPatch,
): Promise<void> {
  const source = get().tasks.find((t) => t.id === sourceId);
  if (!source || !source.recurrence_id) return;

  const overrides = get().overrides[source.recurrence_id] ?? [];
  const eff = applyOverrides(source, occurrenceIso, overrides);

  const fields = buildEffectiveTaskFields(patch, eff, source);
  const newTask = createTask({
    ...fields,
    due_at: patch.due_at ?? (eff?.due_at ?? occurrenceIso),
    recurrence_id: null,
  });

  const { tasks: tasksRepo } = getServices();
  await tasksRepo.insert(newTask);
  set({ tasks: [...get().tasks, newTask] });

  const cancelOverride = createOverride({
    recurrence_id: source.recurrence_id,
    recurrence_id_ref: occurrenceIso,
    range: "single",
    status: "cancelled",
  });
  await upsertSingleOverride(set, get, cancelOverride);
}
